import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  Flex,
  Badge,
  Button,
  Spinner,
  Stack,
  Card,
  CardHeader,
  CardBody,
  Heading,
  HStack,
  Icon,
  Checkbox,
  useColorModeValue
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { MdCheck, MdClose } from 'react-icons/md';
import { FiEye } from 'react-icons/fi';
import { RiMergeCellsHorizontal } from 'react-icons/ri';
import { MergeModel } from 'models';
import { ItemGrid } from 'components';
import { NavigationService } from 'services';
import { ProcessStatus } from 'models/types';
import { ItemModel } from 'models';

export interface DetailedMergeCardProps {
  merge: MergeModel;
  onApprove?: (mergeId: string) => void;
  onReject?: (mergeId: string) => void;
  approveLoading?: boolean;
  rejectLoading?: boolean;
  defaultAccordionOpen?: boolean;
  selectionMode?: boolean;
  selected?: boolean;
  onSelect?: () => void;
}

export default function DetailedMergeCard({
  merge,
  onApprove,
  onReject,
  approveLoading = false,
  rejectLoading = false,
  defaultAccordionOpen = false,
  selectionMode = false,
  selected = false,
  onSelect
}: DetailedMergeCardProps) {
  const navigate = useNavigate();
  const selectedBgColor = useColorModeValue('blue.50', 'blue.900');
  const selectedBorderColor = useColorModeValue('blue.400', 'blue.500');
  const hoverBgColor = useColorModeValue('gray.50', 'gray.700');

  const getStatusColor = (status: ProcessStatus) => {
    switch (status) {
      case ProcessStatus.PENDING:
        return 'yellow';
      case ProcessStatus.APPROVED:
        return 'green';
      case ProcessStatus.REJECTED:
        return 'red';
      default:
        return 'gray';
    }
  };

  // Determine if items are demoted based on merge status
  const isMergedItemDemoted = merge.status === ProcessStatus.PENDING;
  const areBaseItemsDemoted = merge.status === ProcessStatus.APPROVED;

  const handleApprove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onApprove && !approveLoading && !rejectLoading) {
      onApprove(merge.id);
    }
  };

  const handleReject = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onReject && !approveLoading && !rejectLoading) {
      onReject(merge.id);
    }
  };

  const handleItemClick = (item: ItemModel) => {
    const isDemoted = item.id === merge.mergedItem.id ? isMergedItemDemoted : areBaseItemsDemoted;
    navigate(NavigationService.getItemDetailsPath(item.id, item.getDisplayName(), isDemoted));
  };

  const handleArtistClick = (item: ItemModel) => {
    navigate(NavigationService.getArtistDetailsPath(item.artistId, item.getArtistName()));
  };

  const handleViewMerge = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(NavigationService.getMergeDetailsPath(merge.id));
  };

  const handleCardClick = () => {
    if (selectionMode && onSelect) {
      onSelect();
    }
  };

  return (
    <Card
      variant="outline"
      borderRadius="lg"
      cursor={selectionMode ? 'pointer' : 'default'}
      onClick={handleCardClick}
      borderColor={selected ? selectedBorderColor : undefined}
      borderWidth={selected ? '2px' : '1px'}
      bg={selected ? selectedBgColor : undefined}
      _hover={selectionMode ? { bg: selected ? selectedBgColor : hoverBgColor } : undefined}
      transition="all 0.2s"
    >
      <CardHeader borderBottomWidth="1px">
        <Flex direction={{ base: 'column', md: 'row' }} align={{ base: 'flex-start', md: 'center' }} justify="space-between" gap={4}>
          <Stack spacing={1} alignItems="flex-start">
            <HStack spacing={3}>
              {selectionMode && (
                <Checkbox
                  isChecked={selected}
                  onChange={(e) => {
                    e.stopPropagation();
                    onSelect?.();
                  }}
                  size="lg"
                  colorScheme="blue"
                />
              )}
              <Badge colorScheme={getStatusColor(merge.status)}>{merge.status}</Badge>
            </HStack>
          </Stack>
          <HStack spacing={2}>
            {!selectionMode && (
              <Button
                size="sm"
                variant="outline"
                leftIcon={<FiEye />}
                onClick={handleViewMerge}
              >
                View
              </Button>
            )}
            {!selectionMode && merge.status === ProcessStatus.PENDING && (
              <>
                {onApprove && (
                  <Button
                    colorScheme="green"
                    size="sm"
                    leftIcon={approveLoading ? <Spinner size="xs" /> : <MdCheck />}
                    isLoading={approveLoading}
                    isDisabled={rejectLoading}
                    onClick={handleApprove}
                  >
                    Approve
                  </Button>
                )}
                {onReject && (
                  <Button
                    colorScheme="red"
                    size="sm"
                    leftIcon={rejectLoading ? <Spinner size="xs" /> : <MdClose />}
                    isLoading={rejectLoading}
                    isDisabled={approveLoading}
                    onClick={handleReject}
                  >
                    Reject
                  </Button>
                )}
              </>
            )}
          </HStack>
        </Flex>
      </CardHeader>
      <CardBody>
        <Stack gap={0}>
          <Box>
            <HStack spacing={2} mb={3}>
              <Heading fontSize="sm">
                {isMergedItemDemoted ? 'Merged Item' : 'Base Item'}
              </Heading>
              {isMergedItemDemoted && (
                <Badge colorScheme="orange" fontSize="xs">Demoted</Badge>
              )}
            </HStack>
            <ItemGrid
              items={[merge.mergedItem]}
              isLoading={false}
              showFilters={false}
              gridDisplay={true}
              onItemNameClick={handleItemClick}
              onArtistNameClick={handleArtistClick}
              showMetadata={true}
            />
          </Box>
          <Box>
            <Accordion allowToggle defaultIndex={defaultAccordionOpen ? [0] : []}>
              <AccordionItem border="1px" borderColor="gray.200" borderRadius="md">
                <h2>
                  <AccordionButton>
                    <Flex flex="1" textAlign="left" flexDirection="row" gap={1} alignItems="center" py={2}>
                      <Icon as={RiMergeCellsHorizontal} w={3} h={3} />
                      <Heading fontSize="sm">
                        {areBaseItemsDemoted ? 'Base Items (Demoted)' : 'Base Items'} ({merge.baseItems.length})
                      </Heading>
                      {areBaseItemsDemoted && (
                        <Badge colorScheme="orange" fontSize="xs" ml={2}>Demoted</Badge>
                      )}
                    </Flex>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <ItemGrid
                    items={merge.baseItems}
                    isLoading={false}
                    showFilters={false}
                    gridDisplay={true}
                    onItemNameClick={handleItemClick}
                    onArtistNameClick={handleArtistClick}
                    showMetadata={true}
                  />
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Box>
        </Stack>
      </CardBody>
    </Card>
  );
}
